import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
	CheckCircle2,
	XCircle,
	Loader2,
	Users,
	Building,
} from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { getAuthHeaders } from '@/services/api'
import { getApiBaseUrl } from '@/lib/api'
import { motion } from 'framer-motion'
import { useTranslation } from '@/hooks/useTranslation'

interface InviteDetails {
	id: string
	programName: string
	teamName: string
	inviterName: string
	email: string
	expiresAt: string
}

export default function ProgramInviteAccept() {
	const { t } = useTranslation()
	const { token } = useParams<{ token: string }>()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { user, loading: authLoading } = useAuth()
	
	const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(null)
	const [loading, setLoading] = useState(true)
	const [accepting, setAccepting] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [success, setSuccess] = useState(false)
	const autoAcceptAttempted = useRef(false)

	useEffect(() => {
		if (token && !authLoading) {
			fetchInviteDetails()
		}
	}, [token, authLoading])

	const acceptInvite = useCallback(async () => {
		if (!token) return

		setAccepting(true)
		setError(null)

		try {
			const authHeaders = await getAuthHeaders()
			const response = await fetch(
				`${getApiBaseUrl()}/api/invite/program/${token}/accept`,
				{
					method: 'POST',
					headers: {
						...authHeaders,
						'Content-Type': 'application/json',
					},
				}
			)

			const data = await response.json()

			if (response.ok) {
				setSuccess(true)
				// Invalidate programs query to refresh the list
				await queryClient.invalidateQueries({ queryKey: ['programs'] })
				// Redirect to the dashboard after a short delay
				setTimeout(() => {
					navigate('/dashboard')
				}, 2000)
			} else {
				setError(data.error || t('programInviteAccept.failedToAcceptInvitation'))
			}
		} catch (err) {
			setError(t('programInviteAccept.errorOccurredWhileAccepting'))
		} finally {
			setAccepting(false)
		}
	}, [token, queryClient, navigate, t])

	// Auto-accept invitation when user logs in after being redirected from auth
	useEffect(() => {
		// Only auto-accept if:
		// 1. User is logged in
		// 2. Invite details are loaded
		// 3. User hasn't already accepted/declined
		// 4. Email matches (case-insensitive)
		// 5. Not already accepting or in success state
		// 6. Haven't already attempted auto-accept
		if (
			user &&
			inviteDetails &&
			!loading &&
			!accepting &&
			!success &&
			!error &&
			token &&
			!autoAcceptAttempted.current
		) {
			// Check if email matches (case-insensitive)
			const emailMatches = user.email && inviteDetails.email &&
				user.email.toLowerCase() === inviteDetails.email.toLowerCase()

			if (emailMatches) {
				// Auto-accept the invitation
				console.log('[ProgramInviteAccept] Auto-accepting invitation after login')
				autoAcceptAttempted.current = true
				acceptInvite()
			}
		}
	}, [user, inviteDetails, loading, accepting, success, error, token, acceptInvite])

	const fetchInviteDetails = async () => {
		try {
			const response = await fetch(
				`${getApiBaseUrl()}/api/invite/program/${token}`,
				{
					headers: {
						'Content-Type': 'application/json',
					},
				}
			)

			if (response.ok) {
				const data = await response.json()
				setInviteDetails(data)
			} else {
				const error = await response.json()
				setError(error.error || t('programInviteAccept.invalidOrExpiredInvitation'))
			}
		} catch (err) {
			setError(t('programInviteAccept.failedToLoadInvitationDetails'))
		} finally {
			setLoading(false)
		}
	}


	// Loading state
	if (loading || authLoading) {
		return (
			<div className="fixed inset-0 bg-gradient-to-br from-gray-50 via-gray-50 to-gray-100 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
				<Card className="w-full max-w-md mx-auto border-0 shadow-xl bg-white dark:bg-gray-800">
					<CardContent className="py-12">
						<div className="flex flex-col items-center">
							<div className="relative">
								<Loader2 className="h-10 w-10 animate-spin text-primary" />
								<div className="absolute inset-0 h-10 w-10 animate-ping rounded-full bg-primary/20" />
							</div>
							<p className="mt-6 text-sm font-medium text-muted-foreground">
								{t('programInviteAccept.loadingInvitationDetails')}
							</p>
						</div>
					</CardContent>
				</Card>
			</div>
		)
	}

	// Error state
	if (error && !inviteDetails) {
		return (
			<div className="fixed inset-0 bg-gradient-to-br from-gray-50 via-gray-50 to-gray-100 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.4, ease: "easeOut" }}
					className="w-full max-w-md mx-auto"
				>
					<Card className="border-0 shadow-xl bg-white dark:bg-gray-800">
						<CardHeader className="text-center pb-4 pt-8">
							<div className="mx-auto mb-4 w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
								<XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
							</div>
							<CardTitle className="text-xl font-bold">
								{t('programInviteAccept.invitationNotFound')}
							</CardTitle>
							<CardDescription className="mt-2">
								{error}
							</CardDescription>
						</CardHeader>
						<CardContent>
							<p className="text-sm text-center text-muted-foreground">
								{t('programInviteAccept.invitationExpiredOrUsed')}
							</p>
						</CardContent>
						<CardFooter className="flex justify-center pb-8">
							<Button 
								variant="outline" 
								className="px-8"
								onClick={() => navigate('/auth')}
							>
								{t('programInviteAccept.goToLogin')}
							</Button>
						</CardFooter>
					</Card>
				</motion.div>
			</div>
		)
	}

	// Not logged in
	if (!user && inviteDetails) {
		return (
			<div className="fixed inset-0 bg-gradient-to-br from-gray-50 via-gray-50 to-gray-100 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.4, ease: "easeOut" }}
					className="w-full max-w-md mx-auto"
				>
					<Card className="border-0 shadow-xl bg-white dark:bg-gray-800">
						<CardHeader className="text-center pb-4 pt-8">
							<div className="mx-auto mb-4 w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
								<Users className="h-8 w-8 text-blue-600 dark:text-blue-400" />
							</div>
							<CardTitle className="text-xl font-bold">{t('programInviteAccept.signInRequired')}</CardTitle>
							<CardDescription className="mt-2">
								{t('programInviteAccept.acceptInvitationToJoinTeam')}
							</CardDescription>
						</CardHeader>
						<CardContent className="px-8 space-y-4">
							{/* Invitation details */}
							<div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 p-4 space-y-3">
								<div className="text-center">
									<p className="text-sm text-muted-foreground mb-1">{t('programInviteAccept.youreInvitedToJoin')}</p>
									<p className="font-semibold text-lg">{inviteDetails.programName}</p>
								
								</div>
							</div>
							
							{inviteDetails.email && (
								<div className="text-center">
									<p className="text-sm text-muted-foreground">
										{t('programInviteAccept.invitationSentTo')}
									</p>
									<p className="text-sm font-medium">
										{inviteDetails.email}
									</p>
								</div>
							)}
						</CardContent>
						<CardFooter className="flex flex-col gap-3 px-8 pb-8">
							<Button
								className="w-full bg-primary hover:bg-primary/90"
								onClick={() => {
									// Store the invite URL to redirect after login
									localStorage.setItem(
										'redirectAfterLogin',
										window.location.pathname
									)
									navigate('/auth')
								}}
							>
								{t('programInviteAccept.signInToContinue')}
							</Button>
						</CardFooter>
					</Card>
				</motion.div>
			</div>
		)
	}

	// Success state
	if (success) {
		return (
			<div className="fixed inset-0 bg-gradient-to-br from-gray-50 via-gray-50 to-gray-100 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
				<motion.div
					initial={{ opacity: 0, scale: 0.9 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={{ duration: 0.5, ease: "easeOut" }}
					className="w-full max-w-md mx-auto"
				>
					<Card className="border-0 shadow-xl bg-white dark:bg-gray-800">
						<CardHeader className="text-center pb-4 pt-8">
							<motion.div
								initial={{ scale: 0 }}
								animate={{ scale: 1 }}
								transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
								className="mx-auto mb-4 w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center"
							>
								<CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
							</motion.div>
							<CardTitle className="text-xl font-bold">
								{t('programInviteAccept.welcomeToTheTeam')}
							</CardTitle>
							<CardDescription className="mt-2">
								{t('programInviteAccept.successfullyJoined', { programName: inviteDetails?.programName })}
							</CardDescription>
						</CardHeader>
						<CardContent className="pb-8">
							<div className="flex justify-center items-center gap-2">
								<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
								<p className="text-sm text-muted-foreground">
									{t('programInviteAccept.redirectingToDashboard')}
								</p>
							</div>
						</CardContent>
					</Card>
				</motion.div>
			</div>
		)
	}

	// Main invitation acceptance UI
	return (
		<div className="fixed inset-0 bg-gradient-to-br from-gray-50 via-gray-50 to-gray-100 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.4, ease: "easeOut" }}
				className="w-full max-w-md mx-auto"
			>
				{/* Subtle decorative element */}
				<div className="text-center mb-6">
					<p className="text-xs font-medium tracking-wider text-muted-foreground uppercase">
						{t('programInviteAccept.youreInvited')}
					</p>
				</div>

				<Card className="border-0 shadow-xl bg-white dark:bg-gray-800">
					<CardHeader className="text-center pb-4 pt-8">
						<div className="mb-6">
							<CardTitle className="text-2xl font-bold tracking-tight">
								{t('programInviteAccept.pendingInvite')}
							</CardTitle>
						</div>
					</CardHeader>
					
					<CardContent className="px-8 pb-2">
						{/* Invitation summary */}
						<div className="text-center mb-6">
							<p className="text-sm text-muted-foreground leading-relaxed">
								<span className="font-semibold text-foreground">{inviteDetails?.inviterName}</span>
								{' '}{t('programInviteAccept.invitedYouToProject', { programName: inviteDetails?.programName })}
							</p>
						</div>

						{/* Project description */}
						<div className="text-center mb-8">
							<p className="text-sm text-muted-foreground">
								{t('programInviteAccept.joinTeamToCollaborate')}
							</p>
						</div>

						{/* Team collaboration icon */}
						<div className="flex justify-center items-center mb-8">
							<div className="flex -space-x-2">
								<div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 border-2 border-white dark:border-gray-800 flex items-center justify-center">
									<Users className="h-5 w-5 text-white" />
								</div>
								<div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 border-2 border-white dark:border-gray-800 flex items-center justify-center">
									<Building className="h-5 w-5 text-white" />
								</div>
							</div>
						</div>

						{/* Warning for email mismatch (case-insensitive comparison) */}
						{user?.email && inviteDetails?.email && 
						 user.email.toLowerCase() !== inviteDetails.email.toLowerCase() && (
							<Alert className="mb-4 bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800">
								<AlertDescription className="text-sm">
									{t('programInviteAccept.invitationSentToEmail', { email: inviteDetails.email })}{' '}
									{t('programInviteAccept.youreSignedInAs', { email: user?.email })}
								</AlertDescription>
							</Alert>
						)}

						{/* Error display */}
						{error && (
							<Alert variant="destructive" className="mb-4">
								<AlertDescription>{error}</AlertDescription>
							</Alert>
						)}
					</CardContent>

					<CardFooter className="flex gap-3 px-8 pb-8">
						<Button
							variant="ghost"
							className="flex-1"
							onClick={() => navigate('/dashboard')}
							disabled={accepting}
						>
							{t('programInviteAccept.decline')}
						</Button>
						<Button
							className="flex-1 bg-primary hover:bg-primary/90"
							onClick={acceptInvite}
							disabled={accepting || (user?.email && inviteDetails?.email && 
								user.email.toLowerCase() !== inviteDetails.email.toLowerCase())}
						>
							{accepting ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									{t('programInviteAccept.accepting')}
								</>
							) : (
								t('programInviteAccept.acceptInvitation')
							)}
						</Button>
					</CardFooter>
				</Card>

			</motion.div>
		</div>
	)
}